import { ByoAiSettings, TopTrendsAiSettings } from "@apiHooks/aiSearchSettings";
import { EMPTY_AI_SEARCH_STATE, useAiSearchStream } from "@apiHooks/useAiSearchStream";
import { useAiSearchSettings } from "@apiHooks/useAiSearchSettings";
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { FCX, usePlatformContext, usePlatformFeatureFlag } from "@datassential/platform-ui-lib";
import { AISearchPage } from "@models";
import useQueryState from "@hooks/useQueryState";
import {
    AI_SEARCH_ANSWER_PARAM,
    AI_SEARCH_QUERY_PARAM,
    AI_SEARCH_ROUTE_PARAM,
    FEATURE_AI_SEARCH_DS_AI,
} from "@constants/general";

type AiSearchSettingsContextType = {
    isLoading: boolean;
    isActive: boolean;
    isOnlyAnswer: boolean;
    resetSearch: () => void;
    topTrendsSettings: TopTrendsAiSettings | null;
    byoSettings: ByoAiSettings | null;
    /** Which page the query routes to; null until the stream says. */
    page: AISearchPage | null;
    /** The answer so far, growing as tokens arrive. */
    answer: string;
    isAnswerLoading: boolean;
    isRelatedToConsumerPreferences: boolean;
    isRelatedToReportPro: boolean;
    hasFailed: boolean;
}

const AiSearchSettingsContext = createContext<AiSearchSettingsContextType>({
    isLoading: false,
    isActive: false,
    isOnlyAnswer: false,
    resetSearch: () => null,
    topTrendsSettings: null,
    byoSettings: null,
    page: null,
    answer: "",
    isAnswerLoading: false,
    isRelatedToConsumerPreferences: false,
    isRelatedToReportPro: false,
    hasFailed: false,
});

export function useAiSearchSettingsContext() {
    const context = useContext(AiSearchSettingsContext);

    if (!context) {
        throw new Error('useAiSearchSettingsContext must be used within AiSearchSettingsContext.Provider');
    }

    return context;
}

/**
 * Owns the one AI-search stream for the current question.
 *
 * Mounted above the router on purpose: a search starts on the AI-search route
 * and the answer renders on TopTrends or BYO, so the connection has to outlive
 * that navigation. The query is read from whichever param currently holds it —
 * `query` while routing, then `search` (or `searchAnswer` when the question had
 * no page to route to) — and `useAiSearchStream` keys on the text, so moving
 * between them doesn't restart the run.
 *
 * With FEATURE_AI_SEARCH_DS_AI off, settings come from the legacy GetSettings
 * call instead and `page`/`answer` stay empty: AiSearch and AiResultSection make
 * their own GetPage/GetSummary calls on that path.
 */
export const AiSearchSettingsContextProvider: FCX = ({
    children,
}) => {
    const [routeQuery] = useQueryState(AI_SEARCH_ROUTE_PARAM, '');
    const [aiSearch, setAiSearch] = useQueryState(AI_SEARCH_QUERY_PARAM, '');
    const [aiSearchAnswer, setAiSearchAnswer] = useQueryState(AI_SEARCH_ANSWER_PARAM, '');
    const [isAiSearchActive, setIsAiSearchActive] = useState(!!aiSearch.trim() || !!aiSearchAnswer.trim());

    useEffect(() => {
        setIsAiSearchActive(!!aiSearch.trim() || !!aiSearchAnswer.trim());
    }, [aiSearch, aiSearchAnswer]);

    const query = useMemo(
        () => routeQuery.trim() || aiSearch.trim() || aiSearchAnswer.trim(),
        [routeQuery, aiSearch, aiSearchAnswer]
    );

    const { isUserInfoLoaded } = usePlatformContext();
    const isDsAiSearchEnabled = usePlatformFeatureFlag(FEATURE_AI_SEARCH_DS_AI);

    // The flag reads as off until user info loads, so hold both paths until then.
    // An empty query idles whichever path the flag doesn't pick.
    const stream = useAiSearchStream(isUserInfoLoaded && isDsAiSearchEnabled ? query : '');
    const legacy = useAiSearchSettings(isUserInfoLoaded && !isDsAiSearchEnabled ? aiSearch : '');

    const {
        page,
        topTrendsSettings,
        byoSettings,
        answer,
        isSettingsLoading,
        isAnswerLoading,
        isRelatedToConsumerPreferences,
        isRelatedToReportPro,
        hasFailed,
    } = isDsAiSearchEnabled
        ? stream
        : {
            ...EMPTY_AI_SEARCH_STATE,
            topTrendsSettings: legacy.topTrendsSettings,
            byoSettings: legacy.byoSettings,
            isSettingsLoading: legacy.isLoading,
        };

    const resetSearch = useCallback(() => {
        setAiSearch('');
        setAiSearchAnswer('');
    }, [setAiSearch, setAiSearchAnswer]);

    const value = useMemo(
        (): AiSearchSettingsContextType => ({
            isLoading: isSettingsLoading,
            isActive: isAiSearchActive,
            isOnlyAnswer: !!aiSearchAnswer.trim(),
            resetSearch,
            topTrendsSettings,
            byoSettings,
            page,
            answer,
            isAnswerLoading,
            isRelatedToConsumerPreferences,
            isRelatedToReportPro,
            hasFailed,
        }),
        [
            isSettingsLoading,
            isAiSearchActive,
            aiSearchAnswer,
            resetSearch,
            topTrendsSettings,
            byoSettings,
            page,
            answer,
            isAnswerLoading,
            isRelatedToConsumerPreferences,
            isRelatedToReportPro,
            hasFailed,
        ]
    );

    return (
        <AiSearchSettingsContext.Provider value={value}>
            {children}
        </AiSearchSettingsContext.Provider>
    );
};
